// frontend/src/components/columns/CoursePlannerColumn.tsx
import CoursePlanner from '../CoursePlanner/index';
import type { AcademicPlan, EnrolledCourse } from '../../types';

export interface CoursePlannerColumnProps {
  academicPlan: AcademicPlan | null;
  loading: boolean;
  refreshing?: boolean;
  collapsedSemesters: Record<string, boolean>;
  bookmarkedCollapsed: boolean;
  showBookmarkedCourses?: boolean;
  onToggleBookmarks: () => void;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  onApplyTemplate?: (distributionPlan: Record<string, EnrolledCourse[]>) => Promise<void>;
  onClearAllCourses?: () => void;
  showTransferCredits?: boolean;
  onToggleTransferCredits?: () => void;
}

/**
 * Thin layout wrapper around CoursePlanner. Renders the scroll container,
 * the inline "refreshing" indicator, and forwards a small set of non-context
 * props to CoursePlanner. The four planner-subtree providers (DragDrop,
 * Suggestion, PlanOperations, ModalOperations) are mounted by the parent.
 */
export function CoursePlannerColumn({
  academicPlan,
  loading,
  refreshing,
  collapsedSemesters,
  bookmarkedCollapsed,
  showBookmarkedCourses,
  onToggleBookmarks,
  openDropdownId,
  setOpenDropdownId,
  onApplyTemplate,
  onClearAllCourses,
  showTransferCredits,
  onToggleTransferCredits,
}: CoursePlannerColumnProps) {
  return (
    <div className="relative h-full overflow-auto px-2 pr-2 lg:pr-4">
      {refreshing && (
        <div className="sticky top-0 z-10 flex items-center justify-center gap-2 py-2 bg-blue-50/90 border-b border-blue-200 backdrop-blur-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
          <span className="text-sm text-blue-700 font-medium">Updating plan...</span>
        </div>
      )}
      <CoursePlanner
        academicPlan={academicPlan}
        loading={loading}
        collapsedSemesters={collapsedSemesters}
        bookmarkedCollapsed={bookmarkedCollapsed}
        showBookmarkedCourses={showBookmarkedCourses}
        onToggleBookmarks={onToggleBookmarks}
        openDropdownId={openDropdownId}
        setOpenDropdownId={setOpenDropdownId}
        dropdownContext="planner"
        onApplyTemplate={onApplyTemplate}
        onClearAllCourses={onClearAllCourses}
        showPriorCredits={showTransferCredits}
        onTogglePriorCredits={onToggleTransferCredits}
      />
    </div>
  );
}
